import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { MasterUseCaseInterface } from 'application/usecases/MasterUseCase';
import { PropertyUseCaseInterface } from 'application/usecases/PropertyUseCase';
import FallbackImg from 'assets/images/FallbackImg.svg';
import { ListedPropertyDetailMemberAuctionProperty } from 'domain/entities/PropertyEntity';
import { openErrorNoti } from 'presentation/components/Notification';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch, useAppSelector } from 'presentation/redux/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { COMMON } from 'utilities/constants/COMMON';
import { CU01702Modal } from './components/CU017002Modal';
import { CollapseDetail } from './components/Collapse';
import { MarketPrice } from './components/MarketPrice';

interface props {
    propertyUseCase: PropertyUseCaseInterface;
    masterUseCase: MasterUseCaseInterface;
    detailAssessment?: ListedPropertyDetailMemberAuctionProperty;
}

const TAB1Component = ({ propertyUseCase, masterUseCase, detailAssessment }: props) => {
    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const { listedPropertyId } = useParams();
    const navigate = useNavigate();
    const visibleModalCU01702 = useAppSelector((state) => state.CU01702Slice.isVisibleCU01702);
    const [isBookmarked, setIsBookmarked] = useState<boolean>(false);
    const [countLike, setCountLike] = useState<number>(0);
    const [title, setTitle] = useState<string>('');
    const [currentIndex, setCurrentIndex] = useState(0); 
    const storedInput = useAppSelector((state) => state.CU03101Slice.storedInput);

    useEffect(() => {
        if (storedInput) form.setFieldsValue(storedInput);
    }, []);

    useEffect(() => {
        setIsBookmarked(detailAssessment?.currentBookmark ?? false);
        setCountLike(detailAssessment?.liked || 0);
        if (
            detailAssessment?.auctionTime ||
            !detailAssessment?.yourAuctionBid ||
            !detailAssessment.auctionStatus
        ) {
            setTitle('');
        } else {
            let title = '';
            if (detailAssessment?.auctionTime) {
                title = COMMON.EMPTY;
            } else {
                if (detailAssessment?.auctionTime.days > 0) {
                    title = '入札締切まであと' + detailAssessment?.auctionTime.days + '日';
                } else if (
                    detailAssessment?.auctionTime.days < 1 &&
                    detailAssessment?.auctionTime.h > 0
                ) {
                    title = 'あと' + detailAssessment?.auctionTime.h + '時間';
                } else {
                    title = "入札一時停止中";
                }
            }
            setTitle(title);
        }
    }, [detailAssessment]);

    const onclickBookmark = async () => {
        try {
            dispatch(startFetching());
            if (propertyUseCase) {
                const response = await propertyUseCase.memberBookMarkListedProperty({
                    listedPropertyId: Number(listedPropertyId),
                });
                if (response.status == '200') {
                    const likeCount = await propertyUseCase.countLike(Number(listedPropertyId));
                    if (likeCount.status == '200') {
                        setIsBookmarked(!isBookmarked);
                        setCountLike(likeCount.result);
                    }
                }
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    const nextImage = () => {
        if (detailAssessment?.images) {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % detailAssessment.images.length);
        }
    };

    const prevImage = () => {
        if (detailAssessment?.images) {
            setCurrentIndex((prevIndex) => (prevIndex - 1 + detailAssessment.images.length) % detailAssessment.images.length);
        }
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexFlow: 'column',
                paddingLeft: 20,
            }}
            className="CU-009-01-container"
        >
            <section
                style={{
                    height: '100%',
                    width: '100%',
                    overflow: 'auto',
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                <CU01702Modal
                    masterUseCase={masterUseCase}
                    propertyUseCase={propertyUseCase}
                    open={visibleModalCU01702}
                    listedPropertyId={listedPropertyId ?? ''}
                    auctionId={detailAssessment?.auctionId ?? 0}
                />
                <div className="main-container">
                    <div className="title" style={{ textAlign: 'center' }}>
                        {title}
                    </div>

                    <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
                        {detailAssessment?.images?.length === 0 ? (
                            <img src={FallbackImg} alt="fallback" />
                        ) : (
                            <div style={{ position: 'relative', width: '100%' }}>
                                <button
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '10px',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '30px',
                                        height: '30px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={prevImage}
                                >
                                    {'<'}
                                </button>

                                <div style={{ display: 'flex', transition: 'transform 0.5s ease-in-out', transform: `translateX(-${currentIndex * 100}%)`, width: '100%' }}>
                                    {detailAssessment?.images.map((image: any, index: number) => (
                                        <div key={index} style={{ minWidth: '100%', boxSizing: 'border-box' }}>
                                            <img
                                                style={{ width: '100%', height: '300px', objectFit: 'contain' }}
                                                src={image.fileUrl || FallbackImg}
                                                alt={`property-${index}`}
                                            />
                                        </div>
                                    ))}
                                </div>

                                <button
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        zIndex: 1,
                                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '30px',
                                        height: '30px',
                                        cursor: 'pointer',
                                    }}
                                    onClick={nextImage}
                                >
                                    {'>'}
                                </button>
                            </div>
                        )}
                    </div>

                    <MarketPrice
                        detailAssessment={detailAssessment}
                        isBookmarked={isBookmarked}
                        countLike={countLike}
                        onClickBookmark={onclickBookmark}
                    />
                    <CollapseDetail detailAssessment={detailAssessment} />
                </div>
            </section>
        </div>
    );
};

export default TAB1Component;
